// Full updated CoursesPage.js with improved top layout and full-width review section
import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Box,
  Typography,
  Alert,
  Snackbar,
  FormControl,
  Select,
  MenuItem,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ClassRoundedIcon from "@mui/icons-material/ClassRounded";
import {
  tabPanelBoxStyles,
  baseItemSx,
  deptItemSx,
  courseItemSx,
  profItemSx,
} from "../utils/courseTabStyles";
import ThumbUp from "@mui/icons-material/ThumbUp";
import ThumbDown from "@mui/icons-material/ThumbDown";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import FlagIcon from "@mui/icons-material/Flag";
import OutlinedFlagIcon from "@mui/icons-material/OutlinedFlag";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import SortIcon from "@mui/icons-material/Sort";
import PriorityHigh from "@mui/icons-material/PriorityHigh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Lightbulb from "@mui/icons-material/Lightbulb";
import Tutorial from "../components/Tutorial";
import { useAuth } from "../components/AuthContext";
import { useInstitution } from "../components/InstitutionContext";
import {
  fetchDepartments,
  fetchCourses,
  fetchProfessorsByCourse,
  fetchReviews,
  fetchMyProfessorCourses,
  updateDepartment,
  updateCourse,
  updateProfessorCourse,
  fetchInstitutions,
} from "../services/api";
import AddDepartmentDialog from "../components/AddDepartmentDialog";
import AddCourseDialog from "../components/AddCourseDialog";
import AddProfessorDialog from "../components/AddProfessorDialog";
import AddReviewDialog from "../components/AddReviewDialog";
import ActionReasonDialog from "../components/ActionReasonDialog";
import IconTextChip from "../components/IconTextChip";
import IconOnlyChip from "../components/IconOnlyChip";
import TextOnlyChip from "../components/TextOnlyChip";
import EditDepartmentDialog from "../components/EditDepartmentDialog";
import EditCourseDialog from "../components/EditCourseDialog";
import EditProfessorCourseDialog from "../components/EditProfessorCourseDialog";
import api from "../services/api";
import { useLocation, useNavigate } from "react-router-dom";
import { REVIEW_REPORT_REASONS } from "../utils/moderationReasons";
import { isReviewEdited } from "../utils/isEdited";
import EditedTag from "../components/EditedTag";
import {
  FilledButton,
  OutlinedButton,
  LoginPromptAlert,
  InstitutionPromptAlert,
  VerifyEmailPromptAlert,
} from "../components";
import Confetti from "react-confetti";

const CoursesPage = () => {
  const { user, loading, emailVerified, setUser } = useAuth();
  const { selectedInstitution } = useInstitution();
  const verifiedUser = user && emailVerified;
  const unverifiedUser = user && !emailVerified;
  const theme = useTheme();
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("All");
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [professors, setProfessors] = useState([]);
  const [selectedProfessor, setSelectedProfessor] = useState(null);
  const [selectedProfLink, setSelectedProfLink] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [selectedSemester, setSelectedSemester] = useState("all");
  const [errorMessage, setErrorMessage] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const reviewsRequestIdRef = useRef(0);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportReviewId, setReportReviewId] = useState(null);
  const [sortBy, setSortBy] = useState("date");
  const [deptDialogOpen, setDeptDialogOpen] = useState(false);
  const [courseDialogOpen, setCourseDialogOpen] = useState(false);
  const [profDialogOpen, setProfDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [editDepartment, setEditDepartment] = useState(null);
  const [editCourse, setEditCourse] = useState(null);
  const [editProfCourse, setEditProfCourse] = useState(null);
  const [myProfessorCourses, setMyProfessorCourses] = useState([]);
  const [highlightDepartmentId, setHighlightDepartmentId] = useState(null);
  const [highlightCourseId, setHighlightCourseId] = useState(null);
  const [highlightProfessorId, setHighlightProfessorId] = useState(null);
  const [highlightReviewId, setHighlightReviewId] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [showConfetti, setShowConfetti] = useState(false);
  const [rejectedAlertOpen, setRejectedAlertOpen] = useState(false);
  const [rejectedIndex, setRejectedIndex] = useState(0);
  const [institutionName, setInstitutionName] = useState("");
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [tourDisabled, setTourDisabled] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [tourShown, setTourShown] = useState(false);
  const addDeptRef = useRef(null);
  const addCourseRef = useRef(null);
  const addProfRef = useRef(null);
  const addReviewRef = useRef(null);

  const selectedCourseObject = useMemo(
    () => courses.find((c) => c.id === selectedCourse),
    [courses, selectedCourse],
  );

  const rejectedDepartments = useMemo(
    () =>
      departments.filter(
        (d) => d.id !== "all" && d.is_rejected && d.created_by === user?.id,
      ),
    [departments, user],
  );

  const rejectedCourses = useMemo(
    () => courses.filter((c) => c.is_rejected && c.created_by === user?.id),
    [courses, user],
  );

  const rejectedProfessorLinks = useMemo(
    () => myProfessorCourses.filter((l) => l.is_rejected),
    [myProfessorCourses],
  );

  const allRejected = useMemo(
    () => [
      ...rejectedDepartments.map((d) => ({ type: "department", data: d })),
      ...rejectedCourses.map((c) => ({ type: "course", data: c })),
      ...rejectedProfessorLinks.map((l) => ({ type: "professor", data: l })),
    ],
    [rejectedDepartments, rejectedCourses, rejectedProfessorLinks],
  );

  const tourSteps = useMemo(
    () => [
      {
        selector: null,
        title: "Welcome to the Course Page",
        content:
          "Follow this step-by-step tutorial to learn how to use the course page effectively.",
        position: "center",
      },
      {
        selector: addDeptRef,
        title: "Add a Department",
        content: "If a department is not listed, then Add a department.",
      },
      {
        selector: addCourseRef,
        title: "Add a Course",
        content:
          "After choosing a department, use Add a course for missing courses.",
      },
      {
        selector: addProfRef,
        title: "Add a Professor",
        content: "If no professor appears, select Add a professor.",
      },
      {
        selector: addReviewRef,
        title: "Add a Review",
        content: "Once everything is selected, click Add a review.",
      },
      {
        selector: null,
        title: "Ready to Explore?",
        content: "Finish tutorial and start exploring.",
        final: true,
        position: "center",
      },
    ],
    [addDeptRef, addCourseRef, addProfRef, addReviewRef],
  );

  const advanceTutorial = () =>
    setTourStep((prev) => Math.min(prev + 1, tourSteps.length - 1));

  const handleTutorialAction = (stepIndex, action) => () => {
    if (tourOpen && tourStep === stepIndex) {
      advanceTutorial();
    } else if (action) {
      action();
    }
  };

  useEffect(() => {
    setRejectedIndex(0);
  }, [allRejected.length]);

  useEffect(() => {
    setRejectedAlertOpen(allRejected.length > 0);
  }, [allRejected.length]);

  useEffect(() => {
    const nameFromUser = user?.userprofile?.institution?.display_name;
    if (nameFromUser) {
      setInstitutionName(nameFromUser);
      return;
    }
    if (!selectedInstitution) {
      setInstitutionName("");
      return;
    }
    const loadInst = async () => {
      try {
        const list = await fetchInstitutions();
        const inst = list.find(
          (i) => String(i.id) === String(selectedInstitution),
        );
        if (inst) setInstitutionName(inst.display_name);
      } catch (err) {
        console.error("Failed to load institution:", err);
        setErrorMessage("Failed to load institution.");
      }
    };
    loadInst();
  }, [user, selectedInstitution]);

  useEffect(() => {
    if (
      verifiedUser &&
      user?.userprofile &&
      user.userprofile.has_visited_courses_page === false &&
      pageLoaded
    ) {
      setTourOpen(true);
    }
  }, [verifiedUser, user, pageLoaded]);

  useEffect(() => {
    if (tourOpen) setTourShown(true);
  }, [tourOpen]);

  useEffect(() => {
    if (
      verifiedUser &&
      !tourOpen &&
      tourShown &&
      user?.userprofile &&
      user.userprofile.has_visited_courses_page === false
    ) {
      api
        .put("/users/profile/", { profile: { has_visited_courses_page: true } })
        .then(() => {
          setUser((prev) =>
            prev
              ? {
                  ...prev,
                  userprofile: {
                    ...prev.userprofile,
                    has_visited_courses_page: true,
                  },
                }
              : prev,
          );
        })
        .catch((err) => {
          console.error(err);
          setErrorMessage("Failed to update profile.");
        });
    }
  }, [tourOpen, tourShown, user, setUser]);

  const loadAll = useCallback(async () => {
    setPageLoaded(false);
    setDepartments([]);
    setCourses([]);
    setFilteredCourses([]);
    setSelectedCourse(null);
    setSelectedProfessor(null);
    setProfessors([]);
    setReviews([]);
    setSemesters([]);
    setSelectedSemester("all");

    const institutionId =
      user?.userprofile?.institution?.id || selectedInstitution;
    if (!institutionId) {
      setPageLoaded(true);
      return;
    }

    try {
      const dept = await fetchDepartments(institutionId);
      const course = await fetchCourses(institutionId);
      const profLinks = user ? await fetchMyProfessorCourses() : [];
      const allCourses = course.results || course;

      const sortedDepts = dept
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name));
      setDepartments([
        { id: "all", name: "All", is_approved: true },
        ...sortedDepts,
      ]);

      const sortedCourses = allCourses
        .slice()
        .sort((a, b) => a.short_name.localeCompare(b.short_name));

      setCourses(sortedCourses);
      setFilteredCourses(sortedCourses);
      setMyProfessorCourses(profLinks || []);
    } finally {
      setPageLoaded(true);
    }
  }, [user, selectedInstitution]);

  const location = useLocation();

  const sortProfessorsByLastName = (list) =>
    list.slice().sort((a, b) => {
      const lastA = a.name.split(" ").pop().toLowerCase();
      const lastB = b.name.split(" ").pop().toLowerCase();
      return lastA.localeCompare(lastB);
    });

  const handleAddReview = () => {
    if (selectedCourse && selectedProfessor && selectedProfLink) {
      setReviewDialogOpen(true);
    }
  };

  const loadReviews = async (
    professorId,
    semesterId,
    courseIdOverride = null,
  ) => {
    const requestId = ++reviewsRequestIdRef.current;
    setLoadingContent(true);
    try {
      const params = semesterId === "all" ? {} : { semester_id: semesterId };
      const data = await fetchReviews(
        courseIdOverride ?? selectedCourse,
        professorId,
        params,
      );
      if (requestId !== reviewsRequestIdRef.current) return;
      const updated = data.map((r) => ({
        ...r,
        upvoted: r.upvoters?.includes(user?.id),
        downvoted: r.downvoters?.includes(user?.id),
        reported: r.reporters?.includes(user?.id),
      }));
      setReviews(updated);
      setErrorMessage(null);
    } catch (err) {
      if (requestId === reviewsRequestIdRef.current) {
        setErrorMessage("Failed to load reviews.");
        setReviews([]);
      }
    } finally {
      if (requestId === reviewsRequestIdRef.current) {
        setLoadingContent(false);
      }
    }
  };

  const handleDepartmentSelect = async (name) => {
    setSelectedDepartment(name);
    const filtered =
      name === "All"
        ? courses
        : courses.filter((c) => c.department.name === name);
    const sorted = filtered
      .slice()
      .sort((a, b) => a.short_name.localeCompare(b.short_name));
    setFilteredCourses(sorted);

    // reset everything
    setSelectedCourse(null);
    setSelectedProfessor(null);
    setProfessors([]);
    setReviews([]);
    setSemesters([]);
    setSelectedSemester("all");
  };

  const handleCourseSelect = async (id) => {
    setSelectedCourse(id);
    setSelectedProfessor(null); // reset first
    setSelectedProfLink(null);
    setProfessors([]);
    setReviews([]);
    setSemesters([]);

    try {
      const data = await fetchProfessorsByCourse(id);
      const sortedProfessors = sortProfessorsByLastName(data);
      setProfessors(sortedProfessors);

      if (sortedProfessors.length > 0) {
        const first = sortedProfessors[0];
        const firstProfId = first.id;

        // Call this FIRST to ensure reviews/semesters load correctly
        await handleProfessorSelect(firstProfId, id, first.link_id);

        // Then update the selected professor state (no need to call it again in the tab)
        setSelectedProfessor(firstProfId);
        setSelectedProfLink(first.link_id);
      }
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to upvote review.");
    }
  };

  const handleProfessorSelect = async (profId, courseId, linkId = null) => {
    setSelectedProfessor(profId);
    setSelectedSemester("all");
    if (linkId !== null) setSelectedProfLink(linkId);

    const courseToUse = courseId ?? selectedCourse;

    try {
      const res = await api.get(
        `/courses/courses/${courseToUse}/professors/${profId}/semesters/`,
      );
      setSemesters(res.data);
      await loadReviews(profId, "all", courseToUse); // ✅ pass courseId here
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to load semesters.");
    }
  };

  const handleSemesterSelect = async (id) => {
    if (id === selectedSemester) return;
    setSelectedSemester(id);
    await loadReviews(selectedProfessor, id);
  };

  const handleUpvote = async (id) => {
    if (!verifiedUser) return;
    try {
      const res = await api.post(`/courses/reviews/${id}/upvote/`);
      updateVote(id, res.data);
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to load professors.");
    }
  };

  const handleDepartmentAdded = (list) => {
    const sorted = list.slice().sort((a, b) => a.name.localeCompare(b.name));
    setDepartments([{ id: "all", name: "All", is_approved: true }, ...sorted]);
  };

  const handleCourseAdded = (list) => {
    const sorted = list
      .slice()
      .sort((a, b) => a.short_name.localeCompare(b.short_name));
    setCourses(sorted);
    const filtered =
      selectedDepartment === "All"
        ? sorted
        : sorted.filter((c) => c.department.name === selectedDepartment);
    setFilteredCourses(filtered);
  };

  const handleProfessorAdded = async () => {
    if (!selectedCourse) return;
    try {
      const data = await fetchProfessorsByCourse(selectedCourse);
      const sorted = sortProfessorsByLastName(data);
      setProfessors(sorted);
      if (sorted.length > 0) {
        await handleProfessorSelect(
          sorted[0].id,
          selectedCourse,
          sorted[0].link_id,
        );
      }
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to load professors.");
    }
  };

  const handleReviewAdded = async (review, isFirstReview) => {
    await loadReviews(selectedProfessor, selectedSemester);
    if (selectedCourse) {
      try {
        const data = await fetchProfessorsByCourse(selectedCourse);
        setProfessors(sortProfessorsByLastName(data));
      } catch (err) {
        console.error(err);
        setErrorMessage("Failed to load professors.");
      }
    }
    if (review) {
      const sem = review.semester
        ? `${review.semester.term} ${review.semester.year}`
        : "";
      const baseMsg = `Review submitted for ${review.professor_name}, ${review.course_name} (${sem})`;
      const msg = isFirstReview
        ? `🎉 Congratulations on your first review submission! ${baseMsg}`
        : baseMsg;
      setSuccessMessage(msg);
      if (isFirstReview) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 6000);
      }
      setHighlightReviewId(review.id);
      setTimeout(() => setHighlightReviewId(null), 1500);
      setTimeout(() => setSuccessMessage(""), 6000);
      setTimeout(() => {
        const el = document.getElementById(`review-${review.id}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
    }
  };

  const handleDownvote = async (id) => {
    if (!verifiedUser) return;
    try {
      const res = await api.post(`/courses/reviews/${id}/downvote/`);
      updateVote(id, res.data);
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to downvote review.");
    }
  };

  const handleReport = async (id, reason) => {
    if (!verifiedUser) return;
    try {
      const res = await api.post(`/courses/reviews/${id}/report/`, { reason });
      setReviews(
        reviews.map((r) =>
          r.id === id
            ? { ...r, reported: true, report_count: res.data.report_count }
            : r,
        ),
      );
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to report review.");
    }
  };

  const openReportDialog = (id) => {
    setReportReviewId(id);
    setReportDialogOpen(true);
  };

  const closeReportDialog = () => {
    setReportDialogOpen(false);
    setReportReviewId(null);
  };

  const updateVote = (id, data) => {
    setReviews(reviews.map((r) => (r.id === id ? { ...r, ...data } : r)));
  };

  const handleResubmitDepartment = async (id, data) => {
    try {
      await updateDepartment(id, data);
      await loadAll();
      setSuccessMessage("Department updated.");
    } catch (err) {
      console.error("Failed to edit entry", err);
      setErrorMessage("Failed to edit entry.");
    } finally {
      setEditDepartment(null);
    }
  };

  const handleResubmitCourse = async (id, data) => {
    try {
      await updateCourse(id, data);
      await loadAll();
      setSuccessMessage("Course updated.");
    } catch (err) {
      console.error("Failed to edit entry", err);
      setErrorMessage("Failed to edit entry.");
    } finally {
      setEditCourse(null);
    }
  };

  const handleResubmitProfCourse = async (id, data) => {
    try {
      await updateProfessorCourse(id, data);
      await loadAll();
      setSuccessMessage("Professor-course updated.");
    } catch (err) {
      console.error("Failed to edit entry", err);
      setErrorMessage("Failed to edit entry.");
    } finally {
      setEditProfCourse(null);
    }
  };

  const flashHighlight = (setter, id) => {
    setter(id);
    setTimeout(() => setter(null), 1500);
  };

  const gotoRejectedDepartment = (dept) => {
    if (!dept) return;
    handleDepartmentSelect(dept.name);
    flashHighlight(setHighlightDepartmentId, dept.id);
  };

  const gotoRejectedCourse = async (course) => {
    if (!course) return;
    await handleDepartmentSelect(course.department.name);
    await handleCourseSelect(course.id);
    flashHighlight(setHighlightCourseId, course.id);
  };

  const gotoRejectedProfessor = async (link) => {
    if (!link) return;
    const { course } = link;
    await handleDepartmentSelect(course.department.name);
    await handleCourseSelect(course.id);
    const profId = link.professor?.id || link.professor_submission?.id;
    await handleProfessorSelect(profId, course.id, link.id);
    flashHighlight(setHighlightProfessorId, profId);
  };

  const gotoRejectedItem = (entry) => {
    if (!entry) return;
    if (entry.type === "department") {
      gotoRejectedDepartment(entry.data);
    } else if (entry.type === "course") {
      gotoRejectedCourse(entry.data);
    } else {
      gotoRejectedProfessor(entry.data);
    }
  };

  const gotoNextRejected = () => {
    if (allRejected.length === 0) return;
    setRejectedIndex((prev) => {
      const next = (prev + 1) % allRejected.length;
      gotoRejectedItem(allRejected[next]);
      return next;
    });
  };

  const gotoPrevRejected = () => {
    if (allRejected.length === 0) return;
    setRejectedIndex((prev) => {
      const next = (prev - 1 + allRejected.length) % allRejected.length;
      gotoRejectedItem(allRejected[next]);
      return next;
    });
  };

  const getRejectedLabel = (entry) => {
    if (!entry) return "";
    if (entry.type === "department") {
      return entry.data.name;
    }
    if (entry.type === "course") {
      return `${entry.data.short_name} \u2013 ${entry.data.long_name}`;
    }
    const prof =
      entry.data.professor?.name || entry.data.professor_submission?.name;
    const course = entry.data.course?.short_name;
    return `${prof} \u2013 ${course}`;
  };

  const sortedReviews = useMemo(() => {
    const sorted = [...reviews];
    if (sortBy === "date") {
      sorted.sort(
        (a, b) => new Date(b.date_created) - new Date(a.date_created),
      );
    } else if (sortBy === "semester") {
      const termOrder = { Winter: 0, Spring: 1, Summer: 2, Fall: 3 };
      sorted.sort((a, b) => {
        const aSem = a.semester || {};
        const bSem = b.semester || {};
        if (aSem.year !== bSem.year) {
          return (bSem.year || 0) - (aSem.year || 0);
        }
        return (termOrder[aSem.term] || 0) - (termOrder[bSem.term] || 0);
      });
    }
    return sorted;
  }, [reviews, sortBy]);

  useEffect(() => {
    if (loading) return;
    loadAll();
  }, [loading, loadAll]);

  useEffect(() => {
    if (departments.length === 0 || courses.length === 0) return;
    const params = new URLSearchParams(location.search);
    const dept = params.get("dept");
    const course = params.get("course");
    const prof = params.get("prof");
    (async () => {
      if (dept) await handleDepartmentSelect(dept);
      if (course) await handleCourseSelect(Number(course));
      if (prof) await handleProfessorSelect(Number(prof), Number(course));
    })();
  }, [departments, courses, location.search]);

  return (
    <>
      <Box sx={{ px: 3, pb: 5 }}>
        <Box
          sx={{
            pt: 6,
            mb: 2,
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          {/* Left Column */}
          <Box
            sx={{
              flex: 1,
              minWidth: 250,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="h4"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <ClassRoundedIcon sx={{ color: theme.palette.info.main }} />
              Premed Course Review
              {verifiedUser && (
                <Tooltip title="Show tutorial">
                  <IconOnlyChip
                    icon={<Lightbulb fontSize="small" />}
                    color="info"
                    onClick={() => setTourOpen(true)}
                    sx={{ p: 0.5 }}
                  />
                </Tooltip>
              )}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Browse departments and courses below to read and share reviews.
            </Typography>
            {institutionName && (
              <Typography variant="body1" sx={{ fontStyle: "italic", mt: 2 }}>
                {institutionName}
              </Typography>
            )}
          </Box>

          {/* Right Column with button aligned to bottom of left column */}
          {verifiedUser && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                alignItems: "center",
                minWidth: 200,
              }}
            >
              <OutlinedButton
                text="View My Submissions"
                color="info"
                size="large"
                to="/courses/my-submissions"
                sx={{ whiteSpace: "nowrap" }}
              />
            </Box>
          )}
        </Box>

        {!selectedInstitution && (
          <InstitutionPromptAlert
            message="to explore course reviews."
            sx={{ mb: 2 }}
          />
        )}

        {!user && (
          <LoginPromptAlert
            from={`${location.pathname}?${(() => {
              const p = new URLSearchParams();
              if (selectedDepartment && selectedDepartment !== "All")
                p.set("dept", selectedDepartment);
              if (selectedCourse) p.set("course", selectedCourse);
              if (selectedProfessor) p.set("prof", selectedProfessor);
              return p.toString();
            })()}`}
            message="to Add a Review."
            sx={{ mb: 2 }}
          />
        )}

        {unverifiedUser && (
          <VerifyEmailPromptAlert
            message="to Add a Review."
            sx={{ mb: 2 }}
          />
        )}

        <Snackbar
          open={!!errorMessage}
          autoHideDuration={4000}
          onClose={() => setErrorMessage(null)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={() => setErrorMessage(null)}
            severity="error"
            sx={{ width: "100%" }}
          >
            {errorMessage}
          </Alert>
        </Snackbar>
        {/* — end of lifted snippet ———————————————————————————————— */}

        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              alignContent: "center",
              rowGap: 2,
              columnGap: 2,
              mb: 4,
            }}
          >
            <Box sx={tabPanelBoxStyles}>
              <Typography
                variant="h6"
                align="center"
                sx={{
                  py: 1,
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  bgcolor: "info.main",
                  color: "primary.contrastText",
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                Departments
              </Typography>
              <Box sx={{ flex: 1, overflowY: "auto" }}>
                <List sx={{ p: 0 }}>
                  {departments.map((d) => (
                    <ListItemButton
                      key={d.id}
                      selected={selectedDepartment === d.name}
                      onClick={() => handleDepartmentSelect(d.name)}
                      sx={deptItemSx}
                      className={
                        highlightDepartmentId === d.id
                          ? "flash-highlight"
                          : undefined
                      }
                      alignItems="flex-start"
                    >
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 0.5,
                            }}
                          >
                            <span
                              style={{
                                fontWeight:
                                  selectedDepartment === d.name
                                    ? "bold"
                                    : "inherit",
                              }}
                            >
                              {d.name}
                            </span>
                            {!d.is_approved && (
                              <Tooltip title="Pending Approval">
                                <PendingActionsIcon
                                  color="warning"
                                  fontSize="small"
                                  sx={{ ml: 0.5 }}
                                />
                              </Tooltip>
                            )}
                            {d.is_rejected && d.created_by === user?.id && (
                              <IconOnlyChip
                                icon={<PriorityHigh fontSize="small" />}
                                color="error"
                                onClick={() => setEditDepartment(d)}
                              />
                            )}
                          </Box>
                        }
                        sx={{ my: 0 }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
              <Box
                sx={{ mt: "auto", borderTop: 1, borderColor: "divider", p: 1 }}
              >
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  {verifiedUser && (
                    <OutlinedButton
                      size="small"
                      color="info"
                      ref={addDeptRef}
                      onClick={handleTutorialAction(1, () =>
                        setDeptDialogOpen(true),
                      )}
                      text="Add a Department"
                    />
                  )}
                </Box>
              </Box>
            </Box>

            <Box sx={tabPanelBoxStyles}>
              <Typography
                variant="h6"
                align="center"
                sx={{
                  py: 1,
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  bgcolor: "info.main",
                  color: "primary.contrastText",
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                Courses
              </Typography>
              <Box sx={{ flex: 1, overflowY: "auto" }}>
                {filteredCourses.length > 0 ? (
                  <List sx={{ p: 0 }}>
                    {filteredCourses.map((c) => (
                      <ListItemButton
                        key={c.id}
                        selected={selectedCourse === c.id}
                        onClick={() => handleCourseSelect(c.id)}
                        sx={courseItemSx}
                        className={
                          highlightCourseId === c.id
                            ? "flash-highlight"
                            : undefined
                        }
                        alignItems="flex-start"
                      >
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.5,
                              }}
                            >
                              <span
                                style={{
                                  fontWeight:
                                    selectedCourse === c.id
                                      ? "bold"
                                      : "inherit",
                                }}
                              >
                                {c.short_name}
                              </span>
                              {!c.is_approved && (
                                <Tooltip title="Pending Approval">
                                  <PendingActionsIcon
                                    color="warning"
                                    fontSize="small"
                                    sx={{ ml: 0.5 }}
                                  />
                                </Tooltip>
                              )}
                              {c.is_rejected && c.created_by === user?.id && (
                                <IconOnlyChip
                                  icon={<PriorityHigh fontSize="small" />}
                                  color="error"
                                  onClick={() => setEditCourse(c)}
                                  sx={{ p: 0.5 }}
                                />
                              )}
                            </Box>
                          }
                          secondary={c.long_name}
                          sx={{ my: 0 }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                ) : (
                  <Typography align="center" sx={{ py: 2 }}>
                    No courses available
                  </Typography>
                )}
              </Box>
              <Box
                sx={{ mt: "auto", borderTop: 1, borderColor: "divider", p: 1 }}
              >
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  {verifiedUser && (
                    <OutlinedButton
                      size="small"
                      color="info"
                      ref={addCourseRef}
                      onClick={handleTutorialAction(2, () =>
                        setCourseDialogOpen(true),
                      )}
                      text="Add a Course"
                    />
                  )}
                </Box>
              </Box>
            </Box>

            <Box sx={tabPanelBoxStyles}>
              <Typography
                variant="h6"
                align="center"
                sx={{
                  py: 1,
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  bgcolor: "info.main",
                  color: "primary.contrastText",
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                Professors
              </Typography>
              <Box sx={{ flex: 1, overflowY: "auto" }}>
                {professors.length > 0 ? (
                  <List sx={{ p: 0 }}>
                    {professors.map((p) => (
                      <ListItemButton
                        key={p.id}
                        selected={selectedProfessor === p.id}
                        onClick={() =>
                          handleProfessorSelect(p.id, undefined, p.link_id)
                        }
                        sx={profItemSx}
                        className={
                          highlightProfessorId === p.id
                            ? "flash-highlight"
                            : undefined
                        }
                        alignItems="flex-start"
                      >
                        <Box
                          sx={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            pr: 1,
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <span
                                  style={{
                                    fontWeight:
                                      selectedProfessor === p.id
                                        ? "bold"
                                        : "inherit",
                                  }}
                                >
                                  {p.name}
                                </span>
                                {!p.professor_is_verified ||
                                !p.association_is_approved ? (
                                  <Tooltip title="Pending Approval">
                                    <PendingActionsIcon
                                      color="warning"
                                      fontSize="small"
                                      sx={{ ml: 1 }}
                                    />
                                  </Tooltip>
                                ) : null}
                                {p.is_rejected && p.created_by === user?.id && (
                                  <IconOnlyChip
                                    icon={<PriorityHigh fontSize="small" />}
                                    color="error"
                                    onClick={() =>
                                      setEditProfCourse({
                                        id: p.link_id,
                                        course: selectedCourseObject,
                                        professor: p.professor,
                                        professor_submission:
                                          p.professor_submission,
                                        rejection_reason: p.rejection_reason,
                                        previous_submission:
                                          p.previous_submission,
                                      })
                                    }
                                  />
                                )}
                              </Box>
                            }
                            sx={{ flex: 1, my: 0 }}
                          />
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Typography sx={{ fontWeight: "bold" }}>
                              {p.average_rating != null
                                ? p.average_rating.toFixed(1)
                                : "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </ListItemButton>
                    ))}
                  </List>
                ) : (
                  <Typography align="center" sx={{ py: 2 }}>
                    No professors available
                  </Typography>
                )}

                {selectedProfessor === null && professors.length > 0 && (
                  <Typography align="center" sx={{ py: 1 }}>
                    Select a professor to view reviews
                  </Typography>
                )}
              </Box>
              <Box
                sx={{ mt: "auto", borderTop: 1, borderColor: "divider", p: 1 }}
              >
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  {verifiedUser && (
                    <OutlinedButton
                      size="small"
                      color="info"
                      ref={addProfRef}
                      onClick={handleTutorialAction(3, () =>
                        setProfDialogOpen(true),
                      )}
                      disabled={!selectedCourse}
                      text="Add a Professor"
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            mb: 2,
            mt: 2,
          }}
        >
          {rejectedAlertOpen && (
            <Alert severity="warning" sx={{ mr: 2, flex: 1, borderRadius: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {allRejected.length} rejected submission
                {allRejected.length > 1 ? "s" : ""}
                <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
                  <IconOnlyChip
                    icon={<ArrowBackIcon fontSize="small" />}
                    color="warning"
                    onClick={gotoPrevRejected}
                    sx={{ p: 1, mr: 1 }}
                  />
                  <IconOnlyChip
                    icon={<ArrowForwardIcon fontSize="small" />}
                    color="warning"
                    onClick={gotoNextRejected}
                    sx={{ p: 1 }}
                  />
                </Box>
              </Box>
            </Alert>
          )}
          {verifiedUser && (
            <FilledButton
              onClick={handleTutorialAction(4, handleAddReview)}
              size="large"
              color="info"
              ref={addReviewRef}
              disabled={!selectedCourse || !selectedProfessor}
              text="Add a Review"
              sx={{ ml: "auto", flexShrink: 0, whiteSpace: "nowrap" }}
            />
          )}
        </Box>
        <Snackbar
          open={!!successMessage}
          autoHideDuration={4000}
          onClose={() => setSuccessMessage("")}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSuccessMessage("")}
            severity="success"
            sx={{ width: "100%" }}
          >
            {successMessage}
          </Alert>
        </Snackbar>
        {showConfetti && (
          <Confetti width={window.innerWidth} height={window.innerHeight} />
        )}
        <Box
          sx={{
            width: "100%", // still full-width on mobile
            maxWidth: "800px", // never grow beyond 800px
            mx: "auto", // center horizontally
            px: 2, // optional horizontal padding
          }}
        >
          {selectedProfessor && (
            <Typography
              variant="h6"
              sx={{ mb: 2, fontWeight: "bold", textAlign: "center" }}
            >
              {reviews.length === 0
                ? "Reviews"
                : reviews.length === 1
                  ? "1 Review"
                  : `${reviews.length} Reviews`}
            </Typography>
          )}

          {selectedProfessor && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ display: "flex", alignItems: "center" }}>
                  Sort by
                </Typography>
                <SortIcon fontSize="small" />
                <FormControl size="small">
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <MenuItem value="date">Date</MenuItem>
                    <MenuItem value="semester">Semester</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {semesters.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    alignItems: "center",
                  }}
                >
                  <Typography sx={{ display: "flex", alignItems: "center" }}>
                    Filter
                  </Typography>
                  {selectedSemester === "all" ? (
                    <FilledButton
                      size="small"
                      color="info"
                      text="All"
                      onClick={() => handleSemesterSelect("all")}
                    />
                  ) : (
                    <OutlinedButton
                      size="small"
                      color="info"
                      text="All"
                      onClick={() => handleSemesterSelect("all")}
                    />
                  )}
                  {semesters.map((s) =>
                    selectedSemester === s.id ? (
                      <FilledButton
                        size="small"
                        key={s.id}
                        color="info"
                        text={`${s.term} ${s.year}`}
                        onClick={() => handleSemesterSelect(s.id)}
                      />
                    ) : (
                      <OutlinedButton
                        size="small"
                        key={s.id}
                        color="info"
                        text={`${s.term} ${s.year}`}
                        onClick={() => handleSemesterSelect(s.id)}
                      />
                    ),
                  )}
                </Box>
              )}
            </Box>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {sortedReviews.map((r) => (
              <Box
                key={r.id}
                id={`review-${r.id}`}
                className={
                  highlightReviewId === r.id ? "flash-highlight" : undefined
                }
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  padding: 2,
                  position: "relative",
                }}
              >
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box
                    sx={{
                      width: 100,
                      display: "flex",
                      flexDirection: "column",
                      borderRight: "1px solid #e0e0e0",
                      pr: 1,
                    }}
                  >
                    {/* Centered Rating */}
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                    >
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Rating
                        </Typography>
                        <Typography
                          sx={{ fontWeight: "bold", fontSize: "1.5rem" }}
                        >
                          {r.rating}/5
                        </Typography>
                      </Box>
                    </Box>

                    {/* Difficulty + Grade */}
                    <Box sx={{ display: "flex", width: "100%" }}>
                      <Box sx={{ flex: 1, textAlign: "center" }}>
                        <Typography variant="caption" color="text.secondary">
                          Difficulty
                        </Typography>
                        <Typography sx={{ fontWeight: "bold" }}>
                          {r.difficulty_level} / 5
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, textAlign: "center" }}>
                        <Typography variant="caption" color="text.secondary">
                          Grade
                        </Typography>
                        <Typography sx={{ fontWeight: "bold" }}>
                          {r.grade_received !== null &&
                          r.grade_received !== undefined
                            ? r.grade_received
                            : "-"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: -1,
                        mb: -0.5,
                      }}
                    >
                      <TextOnlyChip
                        label={`${r.semester?.term} ${r.semester?.year}`}
                        color={
                          {
                            Spring: "success",
                            Winter: "info",
                            Fall: "warning",
                            Summer: "error",
                          }[r.semester?.term] || "default"
                        }
                      />
                      <IconTextChip
                        icon={
                          r.reported ? (
                            <FlagIcon fontSize="small" />
                          ) : (
                            <OutlinedFlagIcon fontSize="small" />
                          )
                        }
                        label={r.reported ? "Reported" : "Report"}
                        color="error"
                        onClick={() => openReportDialog(r.id)}
                        disabled={!verifiedUser || r.reported}
                        sx={{ ml: "auto" }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        my: 1,
                        flexGrow: 1,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {r.content}
                      {isReviewEdited(r) && <EditedTag />}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: 1,
                        mb: -1,
                      }}
                    >
                      <IconTextChip
                        icon={<CalendarTodayOutlinedIcon fontSize="small" />}
                        label={new Date(r.date_created).toLocaleDateString()}
                        color="default"
                      />
                      <IconTextChip
                        icon={
                          r.upvoted ? (
                            <ThumbUp fontSize="small" />
                          ) : (
                            <ThumbUpOffAltIcon fontSize="small" />
                          )
                        }
                        label={r.upvotes}
                        color="info"
                        onClick={() => handleUpvote(r.id)}
                        disabled={!verifiedUser || loadingContent}
                      />
                      <IconTextChip
                        icon={
                          r.downvoted ? (
                            <ThumbDown fontSize="small" />
                          ) : (
                            <ThumbDownOffAltIcon fontSize="small" />
                          )
                        }
                        label={r.downvotes}
                        color="info"
                        onClick={() => handleDownvote(r.id)}
                        disabled={!verifiedUser || loadingContent}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <ActionReasonDialog
        open={reportDialogOpen}
        onClose={closeReportDialog}
        title="Report Review"
        reasons={REVIEW_REPORT_REASONS}
        onConfirm={(reason) => {
          handleReport(reportReviewId, reason);
          closeReportDialog();
        }}
      />
      <AddDepartmentDialog
        open={deptDialogOpen}
        onClose={() => setDeptDialogOpen(false)}
        onAdded={handleDepartmentAdded}
        institutionId={
          user?.userprofile?.institution?.id || selectedInstitution
        }
        onSuccess={(msg) => setSuccessMessage(msg)}
      />
      <AddCourseDialog
        open={courseDialogOpen}
        onClose={() => setCourseDialogOpen(false)}
        departments={departments.filter((d) => d.id !== "all")}
        institutionId={
          user?.userprofile?.institution?.id || selectedInstitution
        }
        onAdded={handleCourseAdded}
        initialDeptId={
          selectedDepartment !== "All"
            ? departments.find((d) => d.name === selectedDepartment)?.id
            : ""
        }
        onSuccess={(msg) => setSuccessMessage(msg)}
      />
      <AddProfessorDialog
        open={profDialogOpen}
        onClose={() => setProfDialogOpen(false)}
        departmentId={
          courses.find((c) => c.id === selectedCourse)?.department?.id
        }
        courseId={selectedCourse}
        departmentName={
          courses.find((c) => c.id === selectedCourse)?.department?.name ||
          selectedDepartment
        }
        courseName={
          courses.find((c) => c.id === selectedCourse)?.long_name || ""
        }
        courseShortName={
          courses.find((c) => c.id === selectedCourse)?.short_name || ""
        }
        institutionName={institutionName}
        onAdded={handleProfessorAdded}
        onSuccess={(msg) => setSuccessMessage(msg)}
      />
      <AddReviewDialog
        open={reviewDialogOpen}
        onClose={() => setReviewDialogOpen(false)}
        departmentId={
          courses.find((c) => c.id === selectedCourse)?.department?.id
        }
        courseId={selectedCourse}
        professorCourseId={selectedProfLink}
        departmentName={
          courses.find((c) => c.id === selectedCourse)?.department?.name ||
          selectedDepartment
        }
        courseName={
          courses.find((c) => c.id === selectedCourse)?.long_name || ""
        }
        courseShortName={
          courses.find((c) => c.id === selectedCourse)?.short_name || ""
        }
        professorName={
          professors.find((p) => p.id === selectedProfessor)?.name || ""
        }
        onAdded={handleReviewAdded}
        onSuccess={() => {}}
      />
      <EditDepartmentDialog
        open={Boolean(editDepartment)}
        department={editDepartment}
        onClose={() => setEditDepartment(null)}
        onResubmit={handleResubmitDepartment}
      />
      <EditCourseDialog
        open={Boolean(editCourse)}
        course={editCourse}
        onClose={() => setEditCourse(null)}
        onResubmit={handleResubmitCourse}
      />
      <EditProfessorCourseDialog
        open={Boolean(editProfCourse)}
        link={editProfCourse}
        onClose={() => setEditProfCourse(null)}
        onResubmit={handleResubmitProfCourse}
      />
      {tourOpen && (
        <Tutorial
          steps={tourSteps}
          isOpen={tourOpen}
          setIsOpen={setTourOpen}
          currentStep={tourStep}
          setCurrentStep={setTourStep}
          disabledActions={tourDisabled}
          setDisabledActions={setTourDisabled}
        />
      )}
    </>
  );
};

export default CoursesPage;
